/**
 * React Query Hook: Generate PDF
 *
 * Hook for generating PDF reports for a session.
 * Endpoint: POST /api/generate-pdf
 */

import {
  useMutation,
  type UseMutationResult,
  useQueryClient,
} from "@tanstack/react-query";
import { generatePdf } from "@/api/endpoints/pdf";
import { queryKeys } from "@/lib/queryClient";
import { InsufficientCreditsError } from "@/utils/supabaseFunctionsError";
import type { GeneratePdfRequest, GeneratePdfResponse } from "@/api/types/pdf";

/**
 * Hook to generate PDF report for a session
 *
 * Features:
 * - Deducts 150 credits from user's balance (handled by backend)
 * - Automatically invalidates credit balance query after success
 * - Automatically invalidates credit history after success
 * - Handles InsufficientCreditsError gracefully
 * - Provides loading state during mutation
 *
 * Cost: 150 credits per PDF download
 *
 * @returns React Query mutation result
 */
export function useGeneratePdf(): UseMutationResult<
  GeneratePdfResponse,
  Error,
  GeneratePdfRequest,
  unknown
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: generatePdf,
    onSuccess: (data) => {
      console.log(
        `📄 [PDF] Successfully generated PDF report. Template ready for client-side generation.`
      );

      // Invalidate credit balance to trigger refetch
      queryClient.invalidateQueries({
        queryKey: queryKeys.credits.balance(),
      });

      // Also invalidate credit history
      queryClient.invalidateQueries({
        queryKey: queryKeys.credits.all,
      });
    },
    onError: (error) => {
      // InsufficientCreditsError is handled in component
      if (error instanceof InsufficientCreditsError) {
        console.warn(
          `📄 [PDF] Insufficient credits for PDF download. Shortage: ${error.shortage} credits`
        );
        return; // Let component handle showing paywall
      }

      // Log other errors
      console.error("📄 [PDF] Failed to generate PDF:", error);
    },
  });
}
